import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../../../components/Button";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import * as Fonts from "../../../../../../components/Fonts";
import Input from "../../../../../../components/Form/Input/input";
import { FormGroup } from "../../../../../../components/Form/styles";
import { SubHeader } from "../../../../../../components/SubHeader";
import { isEmailValid } from "../../../../../../utils/email";
import { navigationState } from "../../../Sections/navigationState";
import StorageIntegrationServiceInfo from "../../components/StorageIntegrationServiceInfo";
import { FormNavigationState } from "../../formNavigationState";
import MailIcon from "../../icons/Mail";
import { Card, CardFooter, Container, RemoveLink, Section } from "../styles";

type UIState = {
  isShowModalConfirmRemove: boolean;
  isShowModalConfirmEmailChanged: boolean;
};

const EmailStoragePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [uiState, setUIState] = useState<UIState>({
    isShowModalConfirmRemove: false,
    isShowModalConfirmEmailChanged: false,
  });

  const formNavigationState = navigationState.get() as FormNavigationState;

  const section = formNavigationState.section;
  const storage = section.storage;

  const [email, setEmail] = useState(storage.email?.value || "");

  const savedEmail = storage.email?.value || "";
  const isEmailChanged = storage.email.active && savedEmail !== email;
  const hasUnsavedChanges = savedEmail !== email;
  // A new valid e-mail, or the first one when the integration is off
  const canSave = isEmailValid(email) && (hasUnsavedChanges || !storage.email.active);

  const prefix = "pages.editor.form.form.storage";

  const saveHandler = () => {
    if (!canSave) {
      return;
    }
    if (isEmailChanged) {
      setUIState({ ...uiState, isShowModalConfirmEmailChanged: true });
    } else {
      confirmSave();
    }
  };

  const confirmSave = () => {
    updateSectionAndBack(true, email);
  };

  const confirmDelete = () => {
    updateSectionAndBack(false, undefined);
    setUIState({ ...uiState, isShowModalConfirmRemove: false });
  };

  const deleteHandler = () => {
    setUIState({ ...uiState, isShowModalConfirmRemove: true });
  };

  const updateSectionAndBack = (active: boolean, email?: string) => {
    const state: FormNavigationState = {
      ...formNavigationState,
      section: {
        ...section,
        storage: {
          ...section.storage,
          email: { active, value: email },
        },
      },
    };
    navigationState.set(state);
    navigate(-1);
  };

  return (
    <Container>
      <SubHeader
        title="E-mail"
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.form.header.title"), to: "/editor/form" },
        ]}
        hasUnsavedChanges={isEmailChanged || (!storage.email.active && !!email)}
      />

      <Section>
        <StorageIntegrationServiceInfo
          name="E-mail"
          LogoIcon={MailIcon}
          logoContainerColor="var(--color-feedback-danger)"
          info={t(`${prefix}.email.info`)}
          active={storage.email.active}
          statusLabel={storage.email.active ? t(`${prefix}.status.active`) : t(`${prefix}.status.inactive`)}
        />
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.setup`)}</Fonts.Label>
        {/* Saving hands the e-mail back to the form screen, which stores it when the form is saved */}
        <Card
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            saveHandler();
          }}>
          <FormGroup>
            <Input
              id="pageTitle"
              autoCapitalize="none"
              type="email"
              value={email}
              label={t(`${prefix}.email.form.email.label`)}
              onChange={e => setEmail(e.target.value)}
            />
          </FormGroup>
          <CardFooter>
            {hasUnsavedChanges && (
              <LinkButton onClick={() => setEmail(savedEmail)}>{t("pages.settings.unsavedBar.discard")}</LinkButton>
            )}
            <Button type="INFO" size="MD" disabled={!canSave} onClick={saveHandler}>
              {t("actions.save")}
            </Button>
          </CardFooter>
        </Card>
      </Section>

      {storage.email.active && (
        <RemoveLink type="button" onClick={deleteHandler}>
          {t(`${prefix}.email.actions.remove.link`)}
        </RemoveLink>
      )}

      {uiState.isShowModalConfirmRemove && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.form.form.storage.email.actions.remove.confirmModal.title")}
          cancelTitle={t("pages.editor.form.form.storage.email.actions.remove.confirmModal.cancelButton")}
          confirmButtonTitle={t("pages.editor.form.form.storage.email.actions.remove.confirmModal.confirmButton")}
          onConfirm={confirmDelete}
          onCancel={() =>
            setUIState({
              ...uiState,
              isShowModalConfirmRemove: false,
            })
          }
        />
      )}
      {uiState.isShowModalConfirmEmailChanged && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.form.form.storage.email.actions.save.confirmEmailChanged.title")}
          cancelTitle={t("pages.editor.form.form.storage.email.actions.save.confirmEmailChanged.cancelButton")}
          confirmButtonTitle={t("pages.editor.form.form.storage.email.actions.save.confirmEmailChanged.confirmButton")}
          onConfirm={confirmSave}
          onCancel={() =>
            setUIState({
              ...uiState,
              isShowModalConfirmEmailChanged: false,
            })
          }
        />
      )}
    </Container>
  );
};

export default EmailStoragePage;
